import { DangerButton, PrimaryButton } from '@systemeio/ui'
import { useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'
import { ItemIntegrationParams, ItemIntegrationParamsProps } from './item-integration-params'

type CommonProps = {
  removeIntegration: () => Promise<void>
  reconnectIntegration?: () => Promise<void>
  showReconnectButton?: boolean
  onLoading?: (loading: boolean) => void
}

type Props =
  | ({
      email?: never
      createdAt?: never
      status?: never
      customParams: ItemIntegrationParamsProps['params']
    } & CommonProps)
  | ({
      email: string
      createdAt: string
      status: string
      customParams?: never
    } & CommonProps)

export const ItemIntegration = ({
  email,
  status,
  createdAt,
  customParams,
  showReconnectButton,
  removeIntegration,
  reconnectIntegration,
  onLoading,
}: Props) => {
  const { t } = useLocoTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingRemoval, setIsLoadingRemoval] = useState(false)

  const onReconnectIntegration = async () => {
    if (reconnectIntegration && showReconnectButton) {
      setIsLoading(true)
      onLoading?.(true)
      await reconnectIntegration()
      setIsLoading(false)
      onLoading?.(false)
    }
  }

  const onRemoveIntegration = async () => {
    setIsLoadingRemoval(true)
    onLoading?.(true)
    await removeIntegration()
    setIsLoadingRemoval(false)
    onLoading?.(false)
  }

  return (
    <div className={'mb-4 lg:mb-6'}>
      <div className={'mb-5 flex gap-2.5 lg:mb-7 lg:gap-4'}>
        <ItemIntegrationParams
          params={
            customParams ?? [
              { title: t('global.email_address'), value: email },
              { title: t('global.created_on'), value: createdAt },
              { title: t('global.status'), value: status },
            ]
          }
        />
      </div>
      <div
        className={twMerge('flex justify-center', reconnectIntegration && 'justify-evenly gap-2')}
      >
        {reconnectIntegration && showReconnectButton && (
          <PrimaryButton onClick={() => onReconnectIntegration()} isFetching={isLoading}>
            {t('dashboard.user_settings.integrations_page.reconnect')}
          </PrimaryButton>
        )}
        <DangerButton
          disabled={isLoading}
          isFetching={isLoadingRemoval}
          onClick={() => onRemoveIntegration()}
        >
          {t('dashboard.user_settings.integrations_page.disconnect')}
        </DangerButton>
      </div>
    </div>
  )
}
